/**
 * Application state.
 *
 * The store owns exactly one `GatewayConnection` and mirrors what it reports:
 * the session index (the conversation list), the transcript of whichever
 * conversation is open, and the live run for each conversation.
 *
 * Two independent streams describe an in-flight answer, and keeping them apart
 * is what makes the chat feel like a messenger:
 *   - `chat` events carry the token stream of the current run (transient),
 *   - `session.message` events carry the persisted transcript rows (durable).
 * The live run renders as a provisional bubble that is retired the moment its
 * durable row lands, matched on the run id the Gateway stamps on both.
 */
import { create } from "zustand";
import {
  abortRun,
  createSession,
  deleteSession,
  downloadArtifact,
  listModels,
  listSessions,
  loadHistory,
  searchMessages as searchMessagesRpc,
  subscribePush,
  testPush as testPushRpc,
  unsubscribePush,
  vapidPublicKey,
  renameSession,
  sendMessage,
  setSessionModel,
  setSessionArchived,
  setSessionPinned,
  speakText,
  subscribeSessionMessages,
  ttsStatus,
  unsubscribeSessionMessages,
  type SessionScope,
} from "../openclaw/api";
import { GatewayConnection, normalizeGatewayUrl, type ConnectionState } from "../openclaw/connection";
import {
  clearAllDeviceTokens,
  clearConnectionSettings,
  loadConnectionSettings,
  saveConnectionSettings,
  type ConnectionSettings,
} from "../openclaw/credentials";
import { readMissingScopeError } from "@openclaw/gateway-client/browser";
import { clearUnsupportedParams } from "../openclaw/compat";
import { clearDeviceIdentity } from "../openclaw/identity";
import { HISTORY_MORE_PAGE_SIZE, HISTORY_PAGE_SIZE } from "../openclaw/constants";
import {
  createSubscription,
  currentSubscription,
  detectPushSupport,
  readSubscriptionKeys,
} from "../openclaw/push";
import { randomId } from "../lib/id";
import {
  applyTheme,
  loadPreferences,
  savePreferences,
  type Preferences,
} from "../lib/preferences";
import {
  AttachmentError,
  prepareImage,
  readLimits,
  fitsPayload,
  formatBytes,
  DEFAULT_LIMITS,
  type AttachmentLimits,
  type OutgoingAttachment,
} from "../openclaw/attachments";
import { normalizeMessage, normalizeTranscript, type ChatEntry } from "../openclaw/transcript";
import type {
  ChatEvent,
  ChatRunStartupPhase,
  ModelInfo,
  SearchHit,
  SessionMessageEvent,
  SessionRow,
  SessionsChangedEvent,
} from "../openclaw/types";

/** What a send does to a run that is already in flight. */
export type QueueMode = "steer" | "followup" | "interrupt";

/** Live run for one conversation, rendered as a provisional assistant bubble. */
export type LiveRun = {
  runId: string;
  text: string;
  phase?: ChatRunStartupPhase;
  /** False once the run reached a terminal state but its durable row has not landed. */
  active: boolean;
  errorMessage?: string;
  failed?: boolean;
  startedAt: number;
};

type AppState = {
  settings: ConnectionSettings | null;
  connection: GatewayConnection | null;
  connectionState: ConnectionState;

  conversations: SessionRow[];
  conversationsLoading: boolean;
  scope: SessionScope;
  listError?: string;

  activeKey: string | null;
  entries: Record<string, ChatEntry[]>;
  historyLoading: Record<string, boolean>;
  liveRuns: Record<string, LiveRun>;
  /** Text pending in the composer per conversation, so switching chats keeps drafts. */
  drafts: Record<string, string>;
  /** Pictures staged in the composer, alongside the text draft. */
  draftAttachments: Record<string, OutgoingAttachment[]>;
  /** Size ceilings the Gateway advertised in its handshake. */
  attachmentLimits: AttachmentLimits;
  /** Bytes fetched for artifact-backed images, in memory only, keyed by artifact. */
  artifactUrls: Record<string, string>;
  /** True when this Gateway has speech configured, so the read-aloud action shows. */
  ttsAvailable: boolean;
  speaking: boolean;
  /** Models this Gateway can run, loaded the first time the picker opens. */
  models: ModelInfo[];
  modelsLoading: boolean;
  /** This browser's own preferences: theme, Enter behaviour. */
  preferences: Preferences;
  /** Web Push: what this browser can do, and whether it is currently registered. */
  push: {
    supported: boolean;
    reason?: string;
    enabled: boolean;
    busy: boolean;
    /** The Gateway has no VAPID key: this needs setting up before it can work. */
    setupNeeded?: boolean;
  };
  /** Conversations whose transcript is fully loaded, so the button retires. */
  historyExhausted: Record<string, boolean>;
  historyLoadingMore: Record<string, boolean>;
  /** Message search across all conversations, driven by the list's search box. */
  search: {
    query: string;
    hits: SearchHit[];
    loading: boolean;
    indexing: boolean;
  };
  sendError?: string;

  connect: (input: { url: string; token?: string }) => Promise<void>;
  restore: () => void;
  disconnect: (options?: { forgetDevice?: boolean }) => void;

  setScope: (scope: SessionScope) => void;
  refreshConversations: () => Promise<void>;
  openConversation: (key: string | null) => Promise<void>;
  startConversation: (params: { title?: string; message?: string }) => Promise<string | null>;
  send: (key: string, text: string, queueMode?: QueueMode) => Promise<void>;
  attachFiles: (key: string, files: readonly File[]) => Promise<void>;
  removeAttachment: (key: string, id: string) => void;
  loadArtifact: (artifactId: string, sessionKey?: string) => Promise<void>;
  speakLastAnswer: (key: string) => Promise<void>;
  loadModels: () => Promise<void>;
  setModel: (key: string, model: string | null) => Promise<void>;
  loadOlderMessages: (key: string) => Promise<void>;
  searchMessages: (query: string) => Promise<void>;
  setPreference: <K extends keyof Preferences>(key: K, value: Preferences[K]) => void;
  refreshPushState: () => Promise<void>;
  enablePush: () => Promise<void>;
  disablePush: () => Promise<void>;
  testPush: () => Promise<void>;
  stopRun: (key: string) => Promise<void>;
  rename: (key: string, label: string) => Promise<void>;
  archive: (key: string, archived: boolean) => Promise<void>;
  togglePinned: (key: string, pinned: boolean) => Promise<void>;
  remove: (key: string) => Promise<void>;
  setDraft: (key: string, text: string) => void;
  dismissSendError: () => void;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Conversations sort pinned-first, then by most recent activity. */
export function conversationActivityAt(row: SessionRow): number {
  return row.lastInteractionAt ?? row.lastActivityAt ?? row.updatedAt ?? row.createdAt ?? 0;
}

export function sortConversations(rows: SessionRow[]): SessionRow[] {
  return [...rows].sort((a, b) => {
    const pinned = Number(b.pinned === true) - Number(a.pinned === true);
    if (pinned !== 0) {
      return pinned;
    }
    return conversationActivityAt(b) - conversationActivityAt(a);
  });
}

/**
 * A cron session the Gateway opened for a scheduled job that wrote no
 * transcript.
 *
 * Every cron run gets a session of its own, and most of them never produce a
 * chat row — the work happens elsewhere. In the list they sit between real
 * conversations offering to take a first message, which is not something anyone
 * wants to send them. The key is what identifies them (`agent:main:cron:<id>`);
 * a label can be renamed, a key cannot.
 *
 * Emptiness is read from `messageCount` where the Gateway reports it, and from
 * the preview otherwise. On a build that does not know `includeLastMessage`
 * every preview is empty, so the list says how many rows it is holding back
 * rather than letting them disappear on a guess.
 */
export function isEmptyCronRow(row: SessionRow): boolean {
  if (!row.key.split(":").includes("cron")) {
    return false;
  }
  return typeof row.messageCount === "number"
    ? row.messageCount === 0
    : !row.lastMessagePreview?.trim();
}

export function conversationTitle(row: SessionRow): string {
  return (
    row.label?.trim() ||
    row.displayName?.trim() ||
    row.derivedTitle?.trim() ||
    "Neue Unterhaltung"
  );
}

/** Monotonic stamp that lets a stale `sessions.list` reply be discarded. */
let listRequestId = 0;

let refreshTimer: ReturnType<typeof setTimeout> | null = null;
/** The Gateway emits `sessions.changed` per mutation; coalesce the refetches. */
function scheduleRefresh(run: () => void, delayMs = 250): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
  }
  refreshTimer = setTimeout(() => {
    refreshTimer = null;
    run();
  }, delayMs);
}

export const useStore = create<AppState>((set, get) => {
  /** Appends or replaces one durable transcript row. */
  function mergeDurableMessage(sessionKey: string, event: SessionMessageEvent): void {
    const normalized = normalizeMessage(
      event.message,
      event.messageId ?? `live:${event.messageSeq ?? Date.now()}`,
    );
    if (!normalized) {
      return;
    }
    const incoming = normalized.entry;
    if (
      !incoming.text &&
      !incoming.toolActivities.length &&
      !incoming.attachments.length &&
      !incoming.thinking
    ) {
      // A bare tool-result row: fold it into the call it answers. Rows that
      // carry only reasoning or only a picture are *not* bare — they belong in
      // the transcript, and dropping them here hid them until the next reload.
      const existing = get().entries[sessionKey] ?? [];
      if (!normalized.toolResults.length) {
        return;
      }
      const updated = existing.map((entry) => {
        const touched = entry.toolActivities.map((activity) => {
          const match = normalized.toolResults.find((result) => result.id === activity.id);
          return match
            ? { ...activity, result: match.result, isError: match.isError, completed: true }
            : activity;
        });
        return touched.some((activity, index) => activity !== entry.toolActivities[index])
          ? { ...entry, toolActivities: touched }
          : entry;
      });
      set((state) => ({ entries: { ...state.entries, [sessionKey]: updated } }));
      return;
    }

    if (!incoming.timestamp && typeof event.updatedAt === "number") {
      incoming.timestamp = event.updatedAt;
    }

    // A row that lands while a run is in flight is a step of that run's turn,
    // not its reply — the reply is whatever the streaming bubble ends up with.
    // Rows that predate the run keep their answer, so a run started elsewhere
    // never swallows the last answer already on screen.
    if (get().liveRuns[sessionKey]?.active) {
      incoming.duringRun = true;
    }

    set((state) => {
      const existing = state.entries[sessionKey] ?? [];
      const byId = existing.findIndex((entry) => entry.id === incoming.id);
      let next: ChatEntry[];
      if (byId >= 0) {
        next = existing.slice();
        next[byId] = { ...existing[byId], ...incoming };
      } else {
        // Retire the optimistic echo of this exact turn now that it is durable.
        const withoutOptimistic =
          incoming.role === "user"
            ? existing.filter(
                (entry) => !(entry.pending && entry.text.trim() === incoming.text.trim()),
              )
            : existing;
        next = [...withoutOptimistic, incoming];
      }

      // The durable assistant row supersedes the provisional streaming bubble.
      const run = state.liveRuns[sessionKey];
      const retireRun =
        run && !run.active && (incoming.role === "assistant" || event.clientRunId === run.runId);
      const liveRuns = { ...state.liveRuns };
      if (retireRun) {
        delete liveRuns[sessionKey];
      }
      return { entries: { ...state.entries, [sessionKey]: next }, liveRuns };
    });
  }

  function applyChatEvent(event: ChatEvent): void {
    const sessionKey = event.sessionKey;
    if (!sessionKey) {
      return;
    }
    set((state) => {
      const previous = state.liveRuns[sessionKey];
      const base: LiveRun = previous?.runId === event.runId
        ? previous
        : { runId: event.runId, text: "", active: true, startedAt: Date.now() };

      switch (event.state) {
        case "status":
          return {
            liveRuns: { ...state.liveRuns, [sessionKey]: { ...base, active: true, phase: event.phase } },
          };
        case "delta": {
          const text = event.replace ? event.deltaText : `${base.text}${event.deltaText}`;
          return {
            liveRuns: {
              ...state.liveRuns,
              [sessionKey]: { ...base, text, active: true, phase: undefined },
            },
          };
        }
        case "final":
          // Hold the rendered text until the durable row arrives, so the bubble
          // never blinks out and back in between the two streams.
          return {
            liveRuns: { ...state.liveRuns, [sessionKey]: { ...base, active: false, phase: undefined } },
          };
        case "aborted":
          return {
            liveRuns: {
              ...state.liveRuns,
              [sessionKey]: {
                ...base,
                active: false,
                phase: undefined,
                errorMessage: event.errorMessage ?? "Abgebrochen.",
              },
            },
          };
        case "error":
          return {
            liveRuns: {
              ...state.liveRuns,
              [sessionKey]: {
                ...base,
                active: false,
                failed: true,
                phase: undefined,
                errorMessage: event.errorMessage ?? "Der Lauf ist fehlgeschlagen.",
              },
            },
          };
        default:
          return {};
      }
    });

    if (event.state === "final" || event.state === "aborted" || event.state === "error") {
      scheduleRefresh(() => void get().refreshConversations(), 400);

      // A terminal run is normally retired by its durable `session.message`.
      // When that row never arrives — the subscription dropped, or the Gateway
      // suppressed the row — the provisional bubble would sit there forever and
      // duplicate the real one after the next reload. Reconcile against the
      // transcript instead of trusting the stream indefinitely.
      const runId = event.runId;
      setTimeout(() => {
        const run = get().liveRuns[sessionKey];
        if (!run || run.runId !== runId || run.active) {
          return;
        }
        if (get().activeKey === sessionKey) {
          // Re-reading history brings in the durable row and, with it, the
          // authoritative version of what this bubble was showing.
          void get().openConversation(sessionKey);
        }
        set((state) => {
          const liveRuns = { ...state.liveRuns };
          if (liveRuns[sessionKey]?.runId === runId) {
            delete liveRuns[sessionKey];
          }
          return { liveRuns };
        });
      }, 5_000);
    }
  }

  function handleEvent(frame: { event: string; payload?: unknown }): void {
    const payload = frame.payload;
    switch (frame.event) {
      case "chat":
        if (isRecord(payload) && typeof payload.sessionKey === "string") {
          applyChatEvent(payload as unknown as ChatEvent);
        }
        return;
      case "session.message": {
        if (!isRecord(payload)) {
          return;
        }
        const event = payload as SessionMessageEvent;
        const key = event.sessionKey;
        if (key && get().entries[key]) {
          mergeDurableMessage(key, event);
        }
        scheduleRefresh(() => void get().refreshConversations(), 600);
        return;
      }
      case "sessions.changed": {
        const event = isRecord(payload) ? (payload as SessionsChangedEvent) : null;
        const key = event?.sessionKey ?? event?.key;
        if (key && event && typeof event.hasActiveRun === "boolean") {
          set((state) => ({
            conversations: state.conversations.map((row) =>
              row.key === key ? { ...row, hasActiveRun: event.hasActiveRun } : row,
            ),
          }));
        }
        scheduleRefresh(() => void get().refreshConversations());
        return;
      }
      default:
        return;
    }
  }

  function attach(settings: ConnectionSettings): GatewayConnection {
    get().connection?.stop();
    const connection = new GatewayConnection(settings, {
      onState: (connectionState) => set({ connectionState }),
      onEvent: (frame) => handleEvent(frame as { event: string; payload?: unknown }),
      onReady: (hello) => {
        // The handshake carries the ceilings for chat attachments; until it
        // arrives the conservative Gateway defaults apply.
        set({ attachmentLimits: readLimits(hello.policy) });
        // One cheap probe decides whether the read-aloud action is offered at
        // all: a Gateway without a speech provider should not show a button
        // that can only fail.
        void ttsStatus(get().connection!)
          .then((status) => {
            const enabled =
              !isRecord(status) || status.enabled === undefined ? true : status.enabled === true;
            set({ ttsAvailable: enabled });
          })
          .catch(() => set({ ttsAvailable: false }));
        void get().refreshConversations();
        const activeKey = get().activeKey;
        if (activeKey) {
          // Re-subscribe and re-read: events that fired while offline are gone.
          void get().openConversation(activeKey);
        }
      },
    });
    set({ connection, settings, connectionState: connection.getState() });
    connection.start();
    return connection;
  }

  return {
    settings: null,
    connection: null,
    connectionState: { phase: "idle" },
    conversations: [],
    conversationsLoading: false,
    scope: "active",
    activeKey: null,
    entries: {},
    historyLoading: {},
    liveRuns: {},
    drafts: {},
    draftAttachments: {},
    attachmentLimits: DEFAULT_LIMITS,
    artifactUrls: {},
    ttsAvailable: false,
    speaking: false,
    models: [],
    modelsLoading: false,
    preferences: loadPreferences(),
    push: { supported: detectPushSupport().supported, enabled: false, busy: false },
    historyExhausted: {},
    historyLoadingMore: {},
    search: { query: "", hits: [], loading: false, indexing: false },

    async connect({ url, token }) {
      const settings: ConnectionSettings = {
        url: normalizeGatewayUrl(url),
        ...(token?.trim() ? { token: token.trim() } : {}),
      };
      saveConnectionSettings(settings);
      attach(settings);
    },

    restore() {
      const settings = loadConnectionSettings();
      if (settings) {
        attach(settings);
      }
    },

    disconnect(options) {
      get().connection?.stop();
      clearConnectionSettings();
      clearAllDeviceTokens();
      clearUnsupportedParams();
      if (options?.forgetDevice) {
        clearDeviceIdentity();
      }
      set({
        connection: null,
        settings: null,
        connectionState: { phase: "idle" },
        conversations: [],
        entries: {},
        liveRuns: {},
        activeKey: null,
        drafts: {},
        draftAttachments: {},
        artifactUrls: {},
        attachmentLimits: DEFAULT_LIMITS,
      });
    },

    setScope(scope) {
      // Drop the old scope's rows immediately: showing active chats under the
      // "Archiv" tab until the fetch lands reads as a wrong answer, not a slow one.
      set({ scope, activeKey: null, conversations: [], conversationsLoading: true });
      void get().refreshConversations();
    },

    async refreshConversations() {
      const { connection, scope } = get();
      if (!connection?.connected) {
        return;
      }
      // Refreshes are fired from several places at once (events, mutations, the
      // scope switch). Stamp each one so a slow earlier reply cannot overwrite a
      // newer list — including one for a scope the user has since left.
      const requestId = ++listRequestId;
      set({ conversationsLoading: true });
      try {
        const rows = await listSessions(connection, scope);
        if (requestId !== listRequestId || get().scope !== scope) {
          return;
        }
        set({ conversations: sortConversations(rows), conversationsLoading: false, listError: undefined });
      } catch (error) {
        if (requestId !== listRequestId) {
          return;
        }
        set({
          conversationsLoading: false,
          listError: error instanceof Error ? error.message : "Chats could not be loaded.",
        });
      }
    },

    async openConversation(key) {
      const { connection, activeKey } = get();
      if (key === null) {
        set({ activeKey: null });
        return;
      }
      if (activeKey && activeKey !== key && connection?.connected) {
        void unsubscribeSessionMessages(connection, activeKey).catch(() => {});
      }
      set({ activeKey: key });
      if (!connection?.connected) {
        return;
      }
      set((state) => ({
        historyLoading: { ...state.historyLoading, [key]: true },
        // Claim the slot before the round trip. `session.message` is only merged
        // for a conversation the store already knows about, so without this a
        // row landing while the page is in flight — the answer to the message
        // that just created this chat, typically — was dropped on the floor.
        entries: { ...state.entries, [key]: state.entries[key] ?? [] },
      }));
      // What was on screen when the request went out. Anything that appears
      // beside it belongs to a run that is happening right now.
      const knownIds = new Set((get().entries[key] ?? []).map((entry) => entry.id));
      try {
        // Subscribe before reading so no row can land in the gap between them.
        await subscribeSessionMessages(connection, key).catch(() => {});
        const history = await loadHistory(connection, key);
        const rawCount = history.messages?.length ?? 0;
        const entries = normalizeTranscript(history.messages ?? []);
        set((state) => {
          const pageIds = new Set(entries.map((row) => row.id));
          // The page is the tail as of the request, not as of now. Rows that
          // arrived while it was in flight, and an optimistic turn it does not
          // contain yet, survive the swap instead of being overwritten by it.
          const extra = (state.entries[key] ?? []).filter((entry) =>
            entry.pending
              ? !entries.some((row) => row.text.trim() === entry.text.trim())
              : !knownIds.has(entry.id) && !pageIds.has(entry.id),
          );
          return {
            entries: { ...state.entries, [key]: [...entries, ...extra] },
            historyLoading: { ...state.historyLoading, [key]: false },
            // This is the tail again, so whatever was paged in before is gone.
            // A short first page means the whole transcript is already here, and
            // the "older messages" button would have nothing to fetch.
            historyExhausted: {
              ...state.historyExhausted,
              [key]: rawCount < HISTORY_PAGE_SIZE,
            },
          };
        });
      } catch (error) {
        set((state) => ({
          historyLoading: { ...state.historyLoading, [key]: false },
          sendError:
            error instanceof Error ? error.message : "The history could not be loaded.",
        }));
      }
    },

    async startConversation({ title, message }) {
      const { connection } = get();
      if (!connection?.connected) {
        return null;
      }
      try {
        const created = await createSession(connection, {
          ...(title?.trim() ? { label: title.trim() } : {}),
          ...(message?.trim() ? { message: message.trim() } : {}),
        });
        if (created?.key) {
          // Both round trips at once. A create that carries a first message
          // starts its run immediately, so waiting for the list before
          // subscribing is long enough for that run's rows to land unheard —
          // but the chat still needs its list row to render a header.
          const opening = get().openConversation(created.key);
          await get().refreshConversations();
          await opening;
          if (created.runError) {
            set({ sendError: created.runError });
          }
          return created.key;
        }
        await get().refreshConversations();
        return null;
      } catch (error) {
        set({
          sendError:
            error instanceof Error ? error.message : "The conversation could not be created.",
        });
        return null;
      }
    },

    async send(key, text, queueMode) {
      const { connection } = get();
      const trimmed = text.trim();
      const attachments = get().draftAttachments[key] ?? [];
      if (!connection?.connected || (!trimmed && !attachments.length)) {
        return;
      }
      if (attachments.length && !fitsPayload(attachments, trimmed.length, get().attachmentLimits)) {
        set({
          sendError: `This message is too large for the Gateway (limit ${formatBytes(
            get().attachmentLimits.maxPayload,
          )}). Send fewer pictures at once.`,
        });
        return;
      }
      const idempotencyKey = randomId();
      const optimistic: ChatEntry = {
        id: `pending:${idempotencyKey}`,
        role: "user",
        text: trimmed,
        toolActivities: [],
        // The picture is already in hand, so the echo shows it right away.
        attachments: attachments.map((item) => ({
          id: item.id,
          kind: "image" as const,
          mimeType: item.mimeType,
          fileName: item.fileName,
          dataUrl: item.content,
          sizeBytes: item.sizeBytes,
          ...(item.width ? { width: item.width, height: item.height } : {}),
        })),
        timestamp: Date.now(),
        pending: true,
      };
      set((state) => ({
        entries: { ...state.entries, [key]: [...(state.entries[key] ?? []), optimistic] },
        drafts: { ...state.drafts, [key]: "" },
        draftAttachments: { ...state.draftAttachments, [key]: [] },
        sendError: undefined,
      }));

      try {
        await sendMessage(connection, {
          sessionKey: key,
          message: trimmed,
          idempotencyKey,
          ...(attachments.length ? { attachments } : {}),
          ...(queueMode ? { queueMode } : {}),
        });
        set((state) => ({
          entries: {
            ...state.entries,
            [key]: (state.entries[key] ?? []).map((entry) =>
              entry.id === optimistic.id ? { ...entry, pending: false } : entry,
            ),
          },
        }));
        void get().refreshConversations();
      } catch (error) {
        set((state) => ({
          entries: {
            ...state.entries,
            [key]: (state.entries[key] ?? []).map((entry) =>
              entry.id === optimistic.id ? { ...entry, pending: false, failed: true } : entry,
            ),
          },
          // Put the pictures back in the composer: a failed send should leave
          // the user one tap from retrying, not re-picking every file.
          draftAttachments: { ...state.draftAttachments, [key]: attachments },
          sendError:
            error instanceof Error ? error.message : "Die Nachricht konnte nicht gesendet werden.",
        }));
      }
    },

    /**
     * Stages picked, pasted or dropped files in the composer.
     *
     * Each one is validated and, if need be, scaled down here rather than at
     * send time, so a file that cannot travel says so while the user is still
     * looking at the picker.
     */
    async attachFiles(key, files) {
      if (!files.length) {
        return;
      }
      const limits = get().attachmentLimits;
      const prepared: OutgoingAttachment[] = [];
      const failures: string[] = [];

      for (const file of files) {
        try {
          prepared.push(await prepareImage(file, limits, randomId()));
        } catch (error) {
          failures.push(
            error instanceof AttachmentError
              ? error.message
              : `“${file.name}” could not be attached.`,
          );
        }
      }

      set((state) => ({
        draftAttachments: {
          ...state.draftAttachments,
          [key]: [...(state.draftAttachments[key] ?? []), ...prepared],
        },
        ...(failures.length ? { sendError: failures.join(" ") } : {}),
      }));
    },

    removeAttachment(key, id) {
      set((state) => ({
        draftAttachments: {
          ...state.draftAttachments,
          [key]: (state.draftAttachments[key] ?? []).filter((item) => item.id !== id),
        },
      }));
    },

    /**
     * Resolves an artifact-backed image to something renderable.
     *
     * The bytes stay in memory for this session only — the Gateway docs are
     * explicit that a client keeps the reference and not the payload, and a
     * transcript full of base64 in localStorage would be a poor trade anyway.
     */
    async loadArtifact(artifactId, sessionKey) {
      const { connection } = get();
      if (!connection?.connected || get().artifactUrls[artifactId]) {
        return;
      }
      // Claim the slot so a re-render mid-flight does not fetch twice.
      set((state) => ({ artifactUrls: { ...state.artifactUrls, [artifactId]: "" } }));
      try {
        const result = await downloadArtifact(connection, { artifactId, sessionKey });
        const mime = result?.artifact?.mimeType ?? "application/octet-stream";
        const url = result?.data
          ? `data:${mime};base64,${result.data}`
          : (result?.url ?? "");
        set((state) => ({ artifactUrls: { ...state.artifactUrls, [artifactId]: url } }));
      } catch {
        // Leave the empty entry: the view falls back to a plain reference chip
        // instead of retrying against a Gateway that just said no.
      }
    },

    /**
     * Reads the newest answer of a conversation aloud.
     *
     * The Gateway hands back the audio, so it plays here, on the device the
     * user is holding — the call happens from a tap, which is what keeps mobile
     * browsers willing to start playback at all.
     */
    async speakLastAnswer(key) {
      const { connection } = get();
      const entries = get().entries[key] ?? [];
      const answer = [...entries]
        .reverse()
        .find((entry) => entry.role === "assistant" && entry.text.trim());
      if (!connection?.connected || !answer) {
        set({ sendError: "In dieser Unterhaltung gibt es noch keine Antwort zum Vorlesen." });
        return;
      }
      set({ speaking: true });
      try {
        const result = await speakText(connection, answer.text.slice(0, 4_000));
        if (!result?.audioBase64) {
          throw new Error("Das Gateway hat kein Audio geliefert.");
        }
        const audio = new Audio(
          `data:${result.mimeType ?? "audio/mpeg"};base64,${result.audioBase64}`,
        );
        audio.addEventListener("ended", () => set({ speaking: false }));
        audio.addEventListener("error", () => set({ speaking: false }));
        await audio.play();
      } catch (error) {
        set({
          speaking: false,
          sendError:
            error instanceof Error
              ? `Vorlesen fehlgeschlagen: ${error.message}`
              : "Vorlesen fehlgeschlagen.",
        });
      }
    },

    /** Loads the model catalogue once; the picker calls this when it opens. */
    async loadModels() {
      const { connection } = get();
      if (!connection?.connected || get().modelsLoading || get().models.length) {
        return;
      }
      set({ modelsLoading: true });
      try {
        const result = await listModels(connection);
        set({ models: result?.models ?? [], modelsLoading: false });
      } catch (error) {
        set({
          modelsLoading: false,
          sendError:
            error instanceof Error
              ? `Modelle konnten nicht geladen werden: ${error.message}`
              : "Modelle konnten nicht geladen werden.",
        });
      }
    },

    /**
     * Pins one conversation to a model.
     *
     * The row is updated straight away — `sessions.patch` answers before the
     * `sessions.changed` event arrives, and a picker that stays on the old value
     * for half a second reads as a failed tap.
     */
    async setModel(key, model) {
      const { connection } = get();
      if (!connection?.connected) {
        return;
      }
      const previous = get().conversations.find((row) => row.key === key)?.model;
      set((state) => ({
        conversations: state.conversations.map((row) =>
          row.key === key ? { ...row, model: model ?? undefined } : row,
        ),
      }));
      try {
        await setSessionModel(connection, { key, model });
        void get().refreshConversations();
      } catch (error) {
        set((state) => ({
          conversations: state.conversations.map((row) =>
            row.key === key ? { ...row, model: previous } : row,
          ),
          sendError:
            error instanceof Error
              ? `Modellwechsel fehlgeschlagen: ${error.message}`
              : "Modellwechsel fehlgeschlagen.",
        }));
      }
    },

    /**
     * Loads a page of older messages and puts it in front of the transcript.
     *
     * The Gateway documents `offset` without saying which end it counts from,
     * so nothing here depends on the answer: whatever comes back is filtered
     * against the ids already on screen, and a page that adds nothing new marks
     * the conversation as exhausted. Either reading of `offset` therefore ends
     * with a correct transcript and no endless button.
     */
    async loadOlderMessages(key) {
      const { connection } = get();
      const existing = get().entries[key] ?? [];
      if (
        !connection?.connected ||
        get().historyLoadingMore[key] ||
        get().historyExhausted[key] ||
        !existing.length
      ) {
        return;
      }
      set((state) => ({ historyLoadingMore: { ...state.historyLoadingMore, [key]: true } }));
      try {
        const history = await loadHistory(connection, key, {
          limit: HISTORY_MORE_PAGE_SIZE,
          offset: existing.length,
        });
        const page = normalizeTranscript(history.messages ?? []);
        set((state) => {
          const current = state.entries[key] ?? [];
          const known = new Set(current.map((entry) => entry.id));
          const older = page.filter((entry) => !known.has(entry.id));
          return {
            entries: older.length
              ? { ...state.entries, [key]: [...older, ...current] }
              : state.entries,
            historyLoadingMore: { ...state.historyLoadingMore, [key]: false },
            historyExhausted: {
              ...state.historyExhausted,
              [key]: older.length < HISTORY_MORE_PAGE_SIZE,
            },
          };
        });
      } catch (error) {
        set((state) => ({
          historyLoadingMore: { ...state.historyLoadingMore, [key]: false },
          // A failed page should not retire the button, but it should say why.
          sendError:
            error instanceof Error
              ? `Older messages: ${error.message}`
              : "Older messages could not be loaded.",
        }));
      }
    },

    /** Runs a message search over every conversation the Gateway knows. */
    async searchMessages(query) {
      const trimmed = query.trim();
      set((state) => ({ search: { ...state.search, query: trimmed } }));
      const { connection } = get();
      if (!connection?.connected || trimmed.length < 3) {
        set((state) => ({
          search: { ...state.search, hits: [], loading: false, indexing: false },
        }));
        return;
      }
      set((state) => ({ search: { ...state.search, loading: true } }));
      try {
        const result = await searchMessagesRpc(connection, { query: trimmed });
        // A slower earlier search must not overwrite a newer one's hits.
        if (get().search.query !== trimmed) {
          return;
        }
        set({
          search: {
            query: trimmed,
            hits: result?.results ?? [],
            loading: false,
            indexing: result?.indexing === true,
          },
        });
      } catch {
        if (get().search.query === trimmed) {
          // An older Gateway without sessions.search simply yields no hits; the
          // local title filter still works and nothing needs to be shouted.
          set((state) => ({ search: { ...state.search, hits: [], loading: false } }));
        }
      }
    },

    /** Reads back whether this browser already holds a push subscription. */
    /** Stores one preference and applies it right away where that is needed. */
    setPreference(key, value) {
      const preferences = { ...get().preferences, [key]: value };
      savePreferences(preferences);
      if (key === "theme") {
        applyTheme(preferences.theme);
      }
      set({ preferences });
    },

    async refreshPushState() {
      const support = detectPushSupport();
      if (!support.supported) {
        set({ push: { supported: false, reason: support.reason, enabled: false, busy: false } });
        return;
      }
      try {
        const subscription = await currentSubscription();
        set((state) => ({
          push: { ...state.push, supported: true, enabled: Boolean(subscription), busy: false },
        }));
      } catch {
        set((state) => ({ push: { ...state.push, enabled: false, busy: false } }));
      }
    },

    /**
     * Turns on notifications for this browser.
     *
     * Four steps that can each fail on their own: permission, the Gateway's
     * VAPID key, the browser subscription, and registering it. Each failure
     * says which one it was — "es geht nicht" is useless when three of the four
     * are outside this app.
     */
    async enablePush() {
      const { connection } = get();
      const support = detectPushSupport();
      if (!support.supported) {
        set({ push: { supported: false, reason: support.reason, enabled: false, busy: false } });
        return;
      }
      if (!connection?.connected) {
        set({ sendError: "Notifications need the connection to be up." });
        return;
      }

      // A previous attempt may have parked on the setup notice; this one gets to
      // find out for itself.
      set((state) => ({ push: { ...state.push, busy: true, setupNeeded: false } }));
      try {
        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
          throw new Error(
            "The browser refused notifications. That can only be undone in its own settings.",
          );
        }
        const key = await vapidPublicKey(connection);
        if (!key) {
          // Not a failed attempt — a Gateway that was never set up for push. It
          // needs an explanation and a way forward, which a red banner is the
          // wrong shape for, so the settings pane takes it from here.
          set((state) => ({ push: { ...state.push, busy: false, setupNeeded: true } }));
          return;
        }
        const subscription = await createSubscription(key);
        const payload = readSubscriptionKeys(subscription);
        if (!payload) {
          throw new Error("The browser’s push registration came back incomplete.");
        }
        await subscribePush(connection, payload);
        set((state) => ({ push: { ...state.push, enabled: true, busy: false } }));
      } catch (error) {
        set((state) => ({
          push: { ...state.push, busy: false },
          sendError:
            error instanceof Error
              ? `Notifications: ${error.message}`
              : "Notifications could not be set up.",
        }));
      }
    },

    /** Unregisters both ends, so the Gateway stops sending to a dead endpoint. */
    async disablePush() {
      const { connection } = get();
      set((state) => ({ push: { ...state.push, busy: true } }));
      try {
        const subscription = await currentSubscription();
        if (subscription) {
          if (connection?.connected) {
            await unsubscribePush(connection, subscription.endpoint).catch(() => {});
          }
          await subscription.unsubscribe();
        }
        set((state) => ({ push: { ...state.push, enabled: false, busy: false } }));
      } catch (error) {
        set((state) => ({
          push: { ...state.push, busy: false },
          sendError:
            error instanceof Error
              ? `Notifications: ${error.message}`
              : "Notifications could not be turned off.",
        }));
      }
    },

    async testPush() {
      const { connection } = get();
      if (!connection?.connected) {
        return;
      }
      try {
        await testPushRpc(connection);
      } catch (error) {
        set({
          sendError:
            error instanceof Error
              ? `Testbenachrichtigung: ${error.message}`
              : "Testbenachrichtigung fehlgeschlagen.",
        });
      }
    },

    async stopRun(key) {
      const { connection } = get();
      if (!connection?.connected) {
        return;
      }
      try {
        await abortRun(connection, key);
      } catch (error) {
        set({ sendError: error instanceof Error ? error.message : "Abbrechen fehlgeschlagen." });
      }
    },

    async rename(key, label) {
      const { connection } = get();
      if (!connection?.connected) {
        return;
      }
      const trimmed = label.trim();
      set((state) => ({
        conversations: state.conversations.map((row) =>
          row.key === key ? { ...row, label: trimmed || undefined } : row,
        ),
      }));
      try {
        await renameSession(connection, { key, label: trimmed || null });
      } catch (error) {
        set({ sendError: error instanceof Error ? error.message : "Umbenennen fehlgeschlagen." });
      }
      await get().refreshConversations();
    },

    async archive(key, archived) {
      const { connection, conversations } = get();
      if (!connection?.connected) {
        return;
      }
      const row = conversations.find((entry) => entry.key === key);
      try {
        await setSessionArchived(connection, {
          key,
          archived,
          ...(row?.sessionId ? { expectedSessionId: row.sessionId } : {}),
        });
        set((state) => ({
          conversations: state.conversations.filter((entry) => entry.key !== key),
          activeKey: state.activeKey === key ? null : state.activeKey,
        }));
      } catch (error) {
        set({
          sendError:
            error instanceof Error
              ? error.message
              : archived
                ? "Archivieren fehlgeschlagen."
                : "Wiederherstellen fehlgeschlagen.",
        });
      }
      await get().refreshConversations();
    },

    async togglePinned(key, pinned) {
      const { connection } = get();
      if (!connection?.connected) {
        return;
      }
      set((state) => ({
        conversations: sortConversations(
          state.conversations.map((row) => (row.key === key ? { ...row, pinned } : row)),
        ),
      }));
      try {
        await setSessionPinned(connection, { key, pinned });
      } catch (error) {
        set({ sendError: error instanceof Error ? error.message : "Anheften fehlgeschlagen." });
      }
      await get().refreshConversations();
    },

    async remove(key) {
      const { connection, scope } = get();
      if (!connection?.connected) {
        return;
      }
      try {
        // operator.write deletes must target an archived session; archive first
        // so deleting works without operator.admin.
        if (scope !== "archived") {
          const row = get().conversations.find((entry) => entry.key === key);
          await setSessionArchived(connection, {
            key,
            archived: true,
            ...(row?.sessionId ? { expectedSessionId: row.sessionId } : {}),
          });
        }
        await deleteSession(connection, { key, archivedOnly: true });
        set((state) => {
          const entries = { ...state.entries };
          delete entries[key];
          return {
            conversations: state.conversations.filter((entry) => entry.key !== key),
            activeKey: state.activeKey === key ? null : state.activeKey,
            entries,
          };
        });
      } catch (error) {
        // Takes the error itself, not its details: it prefers structured details
        // but falls back to parsing the message, which is what older Gateways
        // send — precisely the builds that demand admin for a delete.
        const missingScope = readMissingScopeError(error);
        set((state) => ({
          // The archive step succeeded even though the delete did not, so the
          // conversation has left the active list; keeping it open would strand
          // the user on a chat that is no longer there.
          ...(missingScope ? { activeKey: state.activeKey === key ? null : state.activeKey } : {}),
          sendError: missingScope
            ? `Deleting for good needs the “${missingScope.missingScope}” right. ` +
              "The conversation has been archived instead. Older Gateways want broader rights " +
              "for this — you can request them in the settings."
            : error instanceof Error
              ? error.message
              : "Deleting failed.",
        }));
      }
      await get().refreshConversations();
    },

    setDraft(key, text) {
      set((state) => ({ drafts: { ...state.drafts, [key]: text } }));
    },

    dismissSendError() {
      set({ sendError: undefined });
    },

  };
});
